import React from "react";
import { motion } from "framer-motion";
import { Avatar, Box, Typography } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import { blue, green, grey } from "@mui/material/colors";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";

import { formatFinance } from "../../../../utils/formatters";

const ComplementItem = styled(motion.div)(({ theme, available = true }) => ({
  borderRadius: theme.spacing(1.5),
  backgroundColor: available ? "#fff" : alpha(grey[100], 0.7),
  border: `1px solid ${
    available ? alpha(grey[300], 0.4) : alpha(grey[300], 0.6)
  }`,
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(1.5),
  cursor: available ? "pointer" : "default",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: available ? alpha(blue[50], 0.3) : alpha(grey[100], 0.7),
    boxShadow: available ? "0 2px 8px rgba(0,0,0,0.04)" : "none",
    transform: available ? "translateY(-2px)" : "none",
    borderColor: available ? alpha(blue[300], 0.4) : alpha(grey[300], 0.6),
  },
}));

const PriceChip = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(green[50], 0.7),
  color: green[700],
  fontWeight: 600,
  fontSize: "0.85rem",
  padding: theme.spacing(0.4, 1.2),
  borderRadius: theme.spacing(1),
  border: `1px solid ${alpha(green[100], 0.5)}`,
  display: "flex",
  alignItems: "center",
  marginRight: theme.spacing(1),
}));

const ActionButton = styled(motion.button)(({ theme, disabled }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: disabled ? alpha(grey[300], 0.5) : alpha(blue[100], 0.2),
  color: disabled ? grey[500] : blue[600],
  border: `1px solid ${
    disabled ? alpha(grey[400], 0.3) : alpha(blue[200], 0.3)
  }`,
  borderRadius: "50%",
  width: 36,
  height: 36,
  cursor: disabled ? "default" : "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: disabled ? alpha(grey[300], 0.5) : alpha(blue[100], 0.4),
  },
}));

const DisabledTag = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(grey[200], 0.7),
  color: grey[600],
  fontWeight: 500,
  fontSize: "0.75rem",
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.spacing(0.8),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
}));

const AddComplementListItem = ({ complement, addComplement, index }) => {
  const handleAddComplement = () => {
    if (complement.available) {
      addComplement(complement);
    }
  };

  return (
    <ComplementItem
      available={complement.available}
      onClick={handleAddComplement}
      whileHover={complement.available ? { y: -2 } : {}}
      whileTap={complement.available ? { scale: 0.98 } : {}}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: complement.available
              ? alpha(blue[100], 0.3)
              : alpha(grey[200], 0.5),
            color: complement.available ? blue[600] : grey[500],
          }}
        >
          {index + 1}
        </Avatar>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            color: complement.available ? grey[800] : grey[500],
          }}
        >
          {complement.title}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {complement.available ? (
          <>
            <PriceChip>{formatFinance(complement.price)}</PriceChip>
            <ActionButton
              component={motion.button}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <AddCircleOutlineIcon fontSize="small" />
            </ActionButton>
          </>
        ) : (
          <DisabledTag>
            <PauseCircleOutlineIcon fontSize="small" />
            INDISPONÍVEL
          </DisabledTag>
        )}
      </Box>
    </ComplementItem>
  );
};

export default AddComplementListItem;
